import React, { useEffect, useState, Children, lazy, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Wind, Sun, Heart, CheckCircle2, Moon, Activity, Shield, Calendar, BookOpen, Utensils, Check, X, Sparkles, ChevronDown, Star, Quote, Lock, Zap, CreditCard, Gift } from 'lucide-react';
// Animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const fadeIn = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 1,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
// Declare fbq for TypeScript
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
export function App() {
  const [modalContent, setModalContent] = useState<'privacy' | 'terms' | null>(null);
  // Initialize Meta Pixel (skip init if already present externally)
  useEffect(() => {
    if ((window as any).__fbPixelInitialized) return;
    (window as any).__fbPixelInitialized = true;
    // If fbq already exists (installed externally), just track PageView
    if (window.fbq) {
      window.fbq('track', 'PageView');
      return;
    }
    // Set up the stub
    const fbq: any = function () {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, arguments);
      } else {
        fbq.queue.push(arguments);
      }
    };
    if (!window._fbq) window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = false;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;
    window.fbq('init', '855275007473565');
    window.fbq('track', 'PageView');
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
  }, []);
  // Track scroll-based pixel events (fire each only once)
  useEffect(() => {
    const firedEvents = new Set<string>();
    const fireOnce = (eventName: string, params: Record<string, any>) => {
      if (firedEvents.has(eventName) || !window.fbq) return;
      firedEvents.add(eventName);
      window.fbq('track', eventName, params);
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const id = entry.target.id;
        if (id === 'pricing') {
          fireOnce('ViewContent', {
            content_name: 'Desbloquea tu Energía Femenina - Pricing',
            content_type: 'product',
            value: 6.39,
            currency: 'USD'
          });
        }
        if (id === 'garantia') {
          fireOnce('AddToWishlist', {
            content_name: 'Desbloquea tu Energía Femenina',
            content_type: 'product',
            value: 6.39,
            currency: 'USD'
          });
        }
        if (id === 'cta-final') {
          fireOnce('Lead', {
            content_name: 'Desbloquea tu Energía Femenina',
            content_type: 'product',
            value: 6.39,
            currency: 'USD'
          });
        }
      });
    }, {
      threshold: 0.3
    });
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const sections = ['pricing', 'garantia', 'cta-final'];
      sections.forEach(id => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });
    }, 500);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  const handlePurchaseClick = () => {
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: 'Desbloquea tu Energía Femenina',
        content_type: 'product',
        value: 6.39,
        currency: 'USD',
        num_items: 1
      });
    }
    // Open Hotmart in a new tab so the pixel has time to fire completely
    window.open('https://pay.hotmart.com/A104327505P', '_blank');
  };
  return <div className="min-h-screen bg-stone-950 text-stone-200 overflow-x-hidden selection:bg-terracotta/30 font-sans" data-id="element-0">
      {/* 🔴 SECCIÓN 1: EL GANCHO BIOLÓGICO (HERO) */}
      <section className="relative pt-12 pb-20 lg:pt-24 lg:pb-32 px-6 overflow-hidden" data-id="element-1">
        {/* Background Glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-terracotta/10 blur-[120px] rounded-full pointer-events-none" data-id="element-2"></div>

        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center relative z-10" data-id="element-3">
          {/* Left Column: Copy */}
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="text-left order-2 lg:order-1" data-id="element-4">
            {/* Pre-headline */}
            <motion.div variants={fadeInUp} className="mb-6" data-id="element-5">
              <p className="text-stone-100 font-medium text-lg md:text-xl leading-relaxed" data-id="element-6">
                ¿Y si el cansancio que sientes no fuera falta de sueño...{' '}
                <span className="text-terracotta" data-id="element-7">
                  sino que tu cuerpo olvidó cómo sentirse a salvo?
                </span>
              </p>
            </motion.div>

            {/* Headline */}
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-[1.15] mb-6 text-stone-50" data-id="element-8">
              Recupera tu vitalidad <br className="hidden md:block" data-id="element-9" />
              <span className="text-terracotta italic" data-id="element-10">en solo 21 días</span>
              <span className="block text-2xl md:text-3xl lg:text-4xl mt-3 text-stone-200 font-light leading-snug" style={{
              fontFamily: '"Nunito", sans-serif'
            }} data-id="element-11">
                Con microhábitos de 2 minutos que resetean tu sistema nervioso
              </span>
            </motion.h1>

            {/* Parenthetical Hook */}
            <motion.p variants={fadeInUp} className="text-lg text-stone-400 font-light italic mb-6" data-id="element-12">
              (aunque hoy sientas que no tienes energía ni para levantarte del
              sofá)
            </motion.p>

            {/* Description */}
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-stone-300 max-w-xl mb-10 font-light leading-relaxed" data-id="element-13">
              Una metodología paso a paso diseñada para la mujer moderna que
              necesita eliminar la niebla mental y el agotamiento crónico sin
              dietas imposibles ni rutinas que te cansen más.
            </motion.p>

            {/* CTA Button */}
            <motion.div variants={fadeInUp} className="flex flex-col items-start gap-4" data-id="element-14">
              <button onClick={scrollToPricing} className="w-full sm:w-auto bg-terracotta text-white px-8 py-5 rounded-full text-lg font-medium hover:bg-terracotta-hover transition-all duration-300 flex items-center justify-center gap-2 group shadow-lg shadow-terracotta/25 hover:shadow-terracotta/35 hover:-translate-y-1" data-id="element-15">
                <Lock className="w-5 h-5" data-id="element-16" />
                SÍ, QUIERO DESBLOQUEAR MI ENERGÍA → ACCESO POR SOLO $6.39
              </button>

              {/* Trust Line */}
              <p className="text-xs md:text-sm text-stone-500 flex flex-wrap items-center gap-2 pl-2 font-medium" data-id="element-17">
                <span data-id="element-18">Pago seguro</span>
                <span className="w-1 h-1 rounded-full bg-stone-600" data-id="element-19"></span>
                <span data-id="element-20">Acceso inmediato</span>
                <span className="w-1 h-1 rounded-full bg-stone-600" data-id="element-21"></span>
                <span data-id="element-22">Garantía de 7 días</span>
              </p>
            </motion.div>
          </motion.div>

          {/* Right Column: Image */}
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="relative order-1 lg:order-2" data-id="element-23">
            <div className="relative rounded-3xl overflow-hidden shadow-xl shadow-stone-400/15 border border-stone-700 bg-stone-900 aspect-[4/5] md:aspect-square lg:aspect-[4/5] max-w-md mx-auto lg:max-w-none group" data-id="element-24">
              <img src="https://cdn.magicpatterns.com/uploads/cx4NVph1bPgPx9AcPvvrtJ/Imagen_1.png" alt="Ebook Desbloquea tu Energía Femenina" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="eager" data-id="element-25" />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/40 via-transparent to-transparent pointer-events-none" data-id="element-26"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 🔴 SECCIÓN 2: EL ESPEJO (CONEXIÓN Y CERCANÍA) */}
      <section className="py-24 px-6 bg-[#F5EDEA] border-y border-terracotta/10 relative overflow-hidden" data-id="element-27">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-terracotta/8 blur-[120px] rounded-full pointer-events-none" data-id="element-28"></div>

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-29">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-10%'
        }} variants={staggerContainer} data-id="element-30">
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif mb-8 text-center text-stone-100" data-id="element-31">
              ¿Te reconoces en esta mujer al final del día?
            </motion.h2>

            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-stone-300 text-center max-w-3xl mx-auto mb-12 font-light leading-relaxed" data-id="element-32">
              Miras el reloj. Son apenas las 4 de la tarde, pero sientes que ya
              no tienes nada más que dar. Tu cuerpo pesa. Tu mente está en otro
              lugar, nublada. Y lo peor no es el cansancio físico... es la
              desconexión.
            </motion.p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-33">
              <motion.div variants={fadeInUp} className="space-y-6" data-id="element-34">
                <EmpathyItem title="Te levantas cansada tras 'dormir' 8 horas" description="Suena la alarma y sientes que un camión te pasó por encima. El café ya no te da energía, solo te mantiene 'alerta' y nerviosa." data-id="element-35" />
                <EmpathyItem title="Vives en una niebla mental constante" description="Intentas trabajar, pero lees la misma frase cinco veces. Olvidas las llaves, olvidas las citas, olvidas por qué entraste a la cocina. Sientes que tu brillo intelectual se está apagando." data-id="element-36" />
              </motion.div>
              <motion.div variants={fadeInUp} className="space-y-6 md:mt-8" data-id="element-37">
                <EmpathyItem title="La irritabilidad te gana la partida" description="Te descubres contestando mal a tu pareja o perdiendo la paciencia con tus hijos por cosas insignificantes. Luego llega la culpa. 'Esa no soy yo', piensas. Tienes razón: es tu sistema nervioso agotado reaccionando por ti." data-id="element-38" />
                <EmpathyItem title="Vives esperando el fin de semana... solo para colapsar" description="Tu vida se ha vuelto una cuenta regresiva para el viernes, solo para pasar el sábado en pijama sin energía para hacer nada de lo que realmente te gusta." data-id="element-39" />
              </motion.div>
            </div>

            <motion.div variants={fadeInUp} className="mt-16 p-8 rounded-3xl bg-stone-950 border border-stone-700 text-center shadow-lg" data-id="element-40">
              <p className="text-xl md:text-2xl font-serif text-terracotta mb-4" data-id="element-41">
                Escúchame bien: No es tu culpa. No eres floja. No te falta
                disciplina.
              </p>
              <p className="text-lg text-stone-300 font-light" data-id="element-42">
                Lo que te pasa es que tu cuerpo está atrapado en Modo
                Supervivencia. Tu sistema nervioso está pidiendo una pausa
                biológica real, no solo "cerrar los ojos".
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 🔴 SECCIÓN 3: EL CORTISOL VS. LA VOLUNTAD */}
      <section className="py-32 px-6 bg-stone-900" data-id="element-43">
        <div className="max-w-4xl mx-auto" data-id="element-44">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-10%'
        }} variants={staggerContainer} className="flex flex-col md:flex-row gap-12 items-center" data-id="element-45">
            <div className="md:w-1/2" data-id="element-46">
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif mb-8 text-stone-100" data-id="element-47">
                La mentira de la fuerza de voluntad
              </motion.h2>

              <motion.div variants={fadeInUp} className="space-y-6 text-lg font-light text-stone-300" data-id="element-48">
                <p data-id="element-49">
                  La cultura actual nos dice que para tener éxito hay que
                  "empujar". Que si estás cansada, necesitas más café y más
                  disciplina. Que debes levantarte a las 5 AM y forzar tu cuerpo
                  al límite.
                </p>
                <p className="font-medium text-stone-200" data-id="element-50">
                  Pero para una mujer con agotamiento, la "disciplina"
                  tradicional es otra forma de violencia contra sí misma.
                </p>
                <p data-id="element-51">
                  Cuando intentas forzar una rutina exigente estando agotada,
                  elevas tu cortisol (la hormona del estrés). Esto inflama tu
                  cuerpo, bloquea tu claridad mental y drena la última gota de
                  vitalidad que te quedaba.
                </p>
                <div className="relative pl-6 border-l-2 border-terracotta py-2 mt-8" data-id="element-52">
                  <p className="text-xl font-serif italic text-terracotta" data-id="element-53">
                    "Desbloquea tu Energía Femenina" no es otra lista de tareas
                    para tu agenda saturada. Es el permiso que tu cuerpo
                    necesitaba para sanar.
                  </p>
                </div>
              </motion.div>
            </div>

            <motion.div variants={fadeIn} className="md:w-1/2" data-id="element-54">
              <div className="relative rounded-2xl overflow-hidden shadow-lg shadow-stone-400/15 border border-stone-700" data-id="element-55">
                <img src="https://cdn.magicpatterns.com/uploads/fnbbHUHuPY67tz2v9ox7mD/Imagen_2.png" alt="Mujer relajada tomando café" className="w-full h-full object-cover" loading="lazy" data-id="element-56" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 🔴 SECCIÓN 4: EL MECANISMO ÚNICO (MICROSEÑALES) */}
      <section className="py-32 px-6 bg-stone-800 border-y border-stone-700/50" data-id="element-57">
        <div className="max-w-6xl mx-auto" data-id="element-58">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="text-center mb-16" data-id="element-59">
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif mb-6 text-stone-100" data-id="element-60">
              Por qué 2 minutos son más poderosos que 2 horas de gimnasio
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-stone-300 max-w-2xl mx-auto font-light" data-id="element-61">
              Este sistema no se basa en "echarle ganas", sino en
              Neurofisiología. Usamos microhábitos de 2 minutos porque:
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-62">
            <MechanismCard icon={<Shield className="w-6 h-6" data-id="element-64" />} title="Cero Resistencia" description="Son tan breves que tu cerebro no puede encender la pereza. No necesitas 'motivación' para algo que dura lo mismo que lavarte la cara." delay={0.1} data-id="element-63" />
            <MechanismCard icon={<Heart className="w-6 h-6" data-id="element-66" />} title="Señales de Seguridad" description="Le enviamos mensajes directos a tu sistema nervioso de que 'estamos a salvo'. Cuando tu cuerpo se siente seguro, desbloquea la energía que tiene guardada para emergencias." delay={0.2} data-id="element-65" />
            <MechanismCard icon={<Moon className="w-6 h-6" data-id="element-68" />} title="Cronobiología Femenina" description="Sincronizamos tu luz, tu respiración y tu movimiento con tus ritmos naturales. Es como nadar a favor de la corriente en lugar de luchar contra ella." delay={0.3} data-id="element-67" />
          </div>
        </div>
      </section>

      {/* 🔴 NUEVA SECCIÓN: HISTORIA DE ORIGEN */}
      <section className="py-32 px-6 bg-stone-900 border-b border-stone-700/50" data-id="element-69">
        <div className="max-w-3xl mx-auto" data-id="element-70">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="bg-stone-950 p-8 md:p-12 rounded-3xl shadow-xl border border-stone-700/50 relative overflow-hidden" data-id="element-71">
            <div className="absolute top-0 right-0 w-64 h-64 bg-terracotta/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-72"></div>

            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif text-stone-100 mb-8 relative z-10" data-id="element-73">
              Por qué creé "Desbloquea tu Energía Femenina"
            </motion.h2>

            <motion.div variants={fadeInUp} className="space-y-6 text-stone-300 font-light leading-relaxed relative z-10" data-id="element-74">
              <p data-id="element-75">
                Hace dos años, yo era la mujer de la que estás leyendo en esta
                página.
              </p>
              <p data-id="element-76">
                Dormía mis horas. Comía "bien". Hacía lo que se supone que debes
                hacer. Y aun así llegaba al mediodía con la sensación de que
                alguien me había quitado la batería.
              </p>
              <p data-id="element-77">
                Fui al médico tres veces. Los análisis salían "normales". Me
                mandaban a casa con el mismo consejo: "descansa más y reduce el
                estrés". Como si eso fuera posible.
              </p>
              <p data-id="element-78">
                Probé rutinas de madrugada. Las abandoné. Probé suplementos
                caros. No funcionaron. Probé meditaciones de 45 minutos. Me
                dormía a los 10.
              </p>
              <p className="text-stone-100 font-medium text-lg py-2 border-l-4 border-terracotta pl-4 my-6" data-id="element-79">
                Hasta que encontré algo en la neurociencia del sistema nervioso
                que lo cambió todo: mi cuerpo no necesitaba más disciplina.
                Necesitaba sentirse seguro.
              </p>
              <p data-id="element-80">
                Empecé a aplicar microseñales de 2 minutos. Pequeñas acciones
                que le decían a mi biología: "estamos a salvo, puedes soltar".
              </p>
              <p data-id="element-81">
                En 7 días noté el primer cambio real. En 21 días me sentí yo de
                nuevo.
              </p>
              <p data-id="element-82">
                Esta guía es lo que desearía haber tenido entonces. Está escrita
                para ti, para la mujer que ya probó de todo y sigue agotada.
                Para la que cree que "es así" y merece saber que no tiene que
                serlo.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 🔴 NUEVA SECCIÓN: TIMELINE DE RESULTADOS */}
      <section className="py-32 px-6 bg-[#F5EDEA] relative overflow-hidden" data-id="element-83">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-terracotta/5 to-transparent opacity-80" data-id="element-84"></div>

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-85">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-86">
            <motion.div variants={fadeInUp} className="text-center mb-16" data-id="element-87">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-terracotta/10 border border-terracotta/20 text-terracotta text-sm font-medium tracking-wide mb-6" data-id="element-88">
                <Calendar className="w-3.5 h-3.5" data-id="element-89" />
                El Proceso
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-50" data-id="element-90">
                Tu hoja de ruta de recuperación
              </h2>
            </motion.div>

            <div className="relative" data-id="element-91">
              {/* Timeline Line */}
              <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-terracotta/20 -translate-x-1/2 hidden md:block" data-id="element-92"></div>
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-terracotta/20 md:hidden" data-id="element-93"></div>

              <div className="space-y-12" data-id="element-94">
                {/* Day 1-3 */}
                <motion.div variants={fadeInUp} className="relative flex flex-col md:flex-row md:items-center md:justify-between group" data-id="element-95">
                  <div className="md:w-5/12 order-2 md:order-1 pl-16 md:pl-0 md:text-right" data-id="element-96">
                    <h3 className="text-xl font-serif text-stone-50 mb-3" data-id="element-97">
                      El primer alivio silencioso
                    </h3>
                    <ul className="space-y-2 text-stone-200 font-light text-sm md:text-base" data-id="element-98">
                      <li className="flex items-start gap-2 md:justify-end" data-id="element-99">
                        <span className="md:hidden text-terracotta mt-1" data-id="element-100">
                          •
                        </span>
                        Menos tensión en cuello y hombros al despertar
                        <span className="hidden md:inline text-terracotta mt-1" data-id="element-101">
                          •
                        </span>
                      </li>
                      <li className="flex items-start gap-2 md:justify-end" data-id="element-102">
                        <span className="md:hidden text-terracotta mt-1" data-id="element-103">
                          •
                        </span>
                        Una sensación sutil de "algo está cambiando"
                        <span className="hidden md:inline text-terracotta mt-1" data-id="element-104">
                          •
                        </span>
                      </li>
                      <li className="flex items-start gap-2 md:justify-end" data-id="element-105">
                        <span className="md:hidden text-terracotta mt-1" data-id="element-106">
                          •
                        </span>
                        Primera noche sin ansiedad nocturna intensa
                        <span className="hidden md:inline text-terracotta mt-1" data-id="element-107">
                          •
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="absolute left-8 md:left-1/2 w-12 h-12 bg-white rounded-full border-4 border-[#F5EDEA] shadow-md flex items-center justify-center -translate-x-1/2 z-10 text-terracotta font-bold text-sm" data-id="element-108">
                    1-3
                  </div>
                  <div className="md:w-5/12 order-1 md:order-3 pl-16 md:pl-0 mb-4 md:mb-0" data-id="element-109">
                    <span className="text-terracotta font-bold tracking-widest uppercase text-sm" data-id="element-110">
                      Día 1–3
                    </span>
                  </div>
                </motion.div>

                {/* Day 7 */}
                <motion.div variants={fadeInUp} className="relative flex flex-col md:flex-row md:items-center md:justify-between group" data-id="element-111">
                  <div className="md:w-5/12 order-1 md:order-1 pl-16 md:pl-0 md:text-right mb-4 md:mb-0" data-id="element-112">
                    <span className="text-terracotta font-bold tracking-widest uppercase text-sm" data-id="element-113">
                      Día 7
                    </span>
                  </div>
                  <div className="absolute left-8 md:left-1/2 w-12 h-12 bg-white rounded-full border-4 border-[#F5EDEA] shadow-md flex items-center justify-center -translate-x-1/2 z-10 text-terracotta font-bold text-sm" data-id="element-114">
                    7
                  </div>
                  <div className="md:w-5/12 order-2 md:order-3 pl-16 md:pl-0" data-id="element-115">
                    <h3 className="text-xl font-serif text-stone-50 mb-3" data-id="element-116">
                      El despertar suave
                    </h3>
                    <ul className="space-y-2 text-stone-200 font-light text-sm md:text-base" data-id="element-117">
                      <li className="flex items-start gap-2" data-id="element-118">
                        <span className="text-terracotta mt-1" data-id="element-119">•</span>
                        La alarma ya no se siente como un ataque
                      </li>
                      <li className="flex items-start gap-2" data-id="element-120">
                        <span className="text-terracotta mt-1" data-id="element-121">•</span>
                        Energía estable en las tardes (sin necesidad de café)
                      </li>
                      <li className="flex items-start gap-2" data-id="element-122">
                        <span className="text-terracotta mt-1" data-id="element-123">•</span>
                        Menos reactividad emocional ante situaciones cotidianas
                      </li>
                    </ul>
                  </div>
                </motion.div>

                {/* Day 14 */}
                <motion.div variants={fadeInUp} className="relative flex flex-col md:flex-row md:items-center md:justify-between group" data-id="element-124">
                  <div className="md:w-5/12 order-2 md:order-1 pl-16 md:pl-0 md:text-right" data-id="element-125">
                    <h3 className="text-xl font-serif text-stone-50 mb-3" data-id="element-126">
                      Claridad que vuelve
                    </h3>
                    <ul className="space-y-2 text-stone-200 font-light text-sm md:text-base" data-id="element-127">
                      <li className="flex items-start gap-2 md:justify-end" data-id="element-128">
                        <span className="md:hidden text-terracotta mt-1" data-id="element-129">
                          •
                        </span>
                        La niebla mental empieza a disiparse
                        <span className="hidden md:inline text-terracotta mt-1" data-id="element-130">
                          •
                        </span>
                      </li>
                      <li className="flex items-start gap-2 md:justify-end" data-id="element-131">
                        <span className="md:hidden text-terracotta mt-1" data-id="element-132">
                          •
                        </span>
                        Puedes concentrarte por bloques más largos
                        <span className="hidden md:inline text-terracotta mt-1" data-id="element-133">
                          •
                        </span>
                      </li>
                      <li className="flex items-start gap-2 md:justify-end" data-id="element-134">
                        <span className="md:hidden text-terracotta mt-1" data-id="element-135">
                          •
                        </span>
                        Las personas a tu alrededor notan que estás "diferente"
                        <span className="hidden md:inline text-terracotta mt-1" data-id="element-136">
                          •
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="absolute left-8 md:left-1/2 w-12 h-12 bg-white rounded-full border-4 border-[#F5EDEA] shadow-md flex items-center justify-center -translate-x-1/2 z-10 text-terracotta font-bold text-sm" data-id="element-137">
                    14
                  </div>
                  <div className="md:w-5/12 order-1 md:order-3 pl-16 md:pl-0 mb-4 md:mb-0" data-id="element-138">
                    <span className="text-terracotta font-bold tracking-widest uppercase text-sm" data-id="element-139">
                      Día 14
                    </span>
                  </div>
                </motion.div>

                {/* Day 21 */}
                <motion.div variants={fadeInUp} className="relative flex flex-col md:flex-row md:items-center md:justify-between group" data-id="element-140">
                  <div className="md:w-5/12 order-1 md:order-1 pl-16 md:pl-0 md:text-right mb-4 md:mb-0" data-id="element-141">
                    <span className="text-terracotta font-bold tracking-widest uppercase text-sm" data-id="element-142">
                      Día 21
                    </span>
                  </div>
                  <div className="absolute left-8 md:left-1/2 w-16 h-16 bg-terracotta rounded-full border-4 border-[#F5EDEA] shadow-lg shadow-terracotta/30 flex items-center justify-center -translate-x-1/2 z-10 text-white font-bold text-lg" data-id="element-143">
                    21
                  </div>
                  <div className="md:w-5/12 order-2 md:order-3 pl-16 md:pl-0" data-id="element-144">
                    <h3 className="text-xl font-serif text-stone-50 mb-3" data-id="element-145">
                      Tú, de regreso
                    </h3>
                    <ul className="space-y-2 text-stone-200 font-light text-sm md:text-base" data-id="element-146">
                      <li className="flex items-start gap-2" data-id="element-147">
                        <span className="text-terracotta mt-1" data-id="element-148">•</span>
                        Energía estable desde que te levantas hasta que te
                        acuestas
                      </li>
                      <li className="flex items-start gap-2" data-id="element-149">
                        <span className="text-terracotta mt-1" data-id="element-150">•</span>
                        Sueño profundo y verdaderamente reparador
                      </li>
                      <li className="flex items-start gap-2" data-id="element-151">
                        <span className="text-terracotta mt-1" data-id="element-152">•</span>
                        Te reconoces en el espejo — no solo físicamente
                      </li>
                      <li className="flex items-start gap-2" data-id="element-153">
                        <span className="text-terracotta mt-1" data-id="element-154">•</span>
                        Tienes ganas de proyectar, de reír, de vivir
                      </li>
                    </ul>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 🔴 SECCIÓN 5: TESTIMONIOS REALES */}
      <section className="py-32 px-6 bg-[#F5EDEA] relative overflow-hidden" data-id="element-155">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-terracotta/8 blur-[150px] rounded-full pointer-events-none" data-id="element-156"></div>

        <div className="max-w-6xl mx-auto relative z-10" data-id="element-157">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-158">
            <motion.div variants={fadeInUp} className="text-center mb-16" data-id="element-159">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-terracotta/10 border border-terracotta/20 text-terracotta text-sm font-medium tracking-wide mb-6" data-id="element-160">
                <Star className="w-3.5 h-3.5" data-id="element-161" />
                Historias Reales
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-50" data-id="element-162">
                Ellas recuperaron su brillo. Tú también puedes.
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-163">
              <TestimonialCard headline="El martes de la segunda semana lloré de alivio. Por primera vez en 3 años tuve energía a las 5 PM." paragraphs={['Antes de este programa, mi rutina era: alarma, café, arrastrarme al trabajo, sobrevivir, llegar a casa destruida, dormir sin descansar. Repetir. Pensé que era mi destino como enfermera y mamá de dos.', "Compré 'Desbloquea tu Energía Femenina' un domingo a las 11 de la noche, entre desesperada y escéptica. El primer microhábito de respiración lo hice el lunes antes de salir. Sentí algo diferente. No dramático. Solo... menos ruido interno.", 'Para el día 10, mi jefa me preguntó si me había ido de vacaciones. No. Solo había dejado de pelear contra mi propio cuerpo.', 'Hoy, tres meses después, sigo usando los hábitos. Son tan simples que nunca los he abandonado.']} name="Carolina M., 34 años" detail="Enfermera | Bogotá, Colombia" tag="Día 10 del programa" delay={0.1} data-id="element-164" />
              <TestimonialCard headline="Tardaba 3 horas en hacer lo que antes hacía en 45 minutos. Este programa me devolvió mi cerebro." paragraphs={['Soy diseñadora freelance. Mi mente es mi negocio. Y la estaba perdiendo. La niebla mental era tan densa que mis clientes empezaron a notarlo. Tardaba una eternidad en tomar decisiones simples. Estaba pensando en dejar mi carrera.', 'Probé yoga de 1 hora. Lo dejé al cuarto día porque terminaba más cansada. Probé suplementos. Nada.', "Con 'Desbloquea tu Energía Femenina' entendí que el problema no era mi cerebro, era mi cortisol disparado. Los microhábitos de 2 minutos eran tan cortos que no tenía excusa para no hacerlos ni en mis días más caóticos.", 'En la semana 2, volví a terminar proyectos con tiempo de sobra. Hoy cobro más porque entrego más rápido y con más calidad. $6 que salvaron mi carrera.']} name="Sofía L., 28 años" detail="Diseñadora Freelance | Santiago, Chile" tag="Completó los 21 días" delay={0.2} data-id="element-165" />
              <TestimonialCard headline="Mi hija de 6 años me dijo 'mamá, estás feliz'. Eso lo dice todo." paragraphs={['Trabajo desde casa con dos niños pequeños. Mi nivel de irritabilidad era una vergüenza para mí misma. Explotaba por el ruido, por el desorden, por cualquier cosa. Luego me encerraba en el baño a llorar de culpa.', "Mi pareja me sugirió 'Desbloquea tu Energía Femenina'. Yo pensé: 'otro PDF motivacional que no sirve'. Lo compré más por él que por convencimiento propio.", 'El Bono del Kit SOS fue lo primero que me cambió la vida. Los protocolos de 2 minutos para momentos de colapso me sacaron de situaciones que antes terminaban en gritos.', "Para el día 18, mi hija mayor me miró y me dijo 'mamá, estás feliz'. Tuve que salir de la habitación para que no me viera llorar. Pero esta vez de alivio."]} name="Valentina R., 31 años" detail="Mamá + Emprendedora | Ciudad de México" tag="Día 18 del programa" delay={0.3} data-id="element-166" />
              <TestimonialCard headline="Dudé porque era muy barato. Pensé que sería basura. Me equivoqué completamente." paragraphs={['Honestamente, $6 me parecía sospechoso. Pensé que sería un PDF de 10 páginas sacado de Google. Compré casi por curiosidad.', 'Son 60 páginas de contenido que se siente como si alguien te hubiera escrito una carta personal. No hay teoría vacía. Hay pasos. Hay razones. Hay ciencia explicada en humano.', 'Y los bonos son productos completos por sí solos. El Mapa de Energía Personal me hizo entender en 15 minutos que mi problema principal era el insomnio ansioso, no la alimentación. Cambié UNA cosa y en una semana dormía diferente.', 'Vale muchísimo más de lo que cuesta. Sin duda.']} name="María José A., 39 años" detail="Contadora | Guadalajara, México" tag="Completó los 21 días" delay={0.4} data-id="element-167" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* 🔴 SECCIÓN 6: SEGURIDAD Y GARANTÍA */}
      <section id="garantia" className="py-24 px-6 bg-stone-900 border-y border-stone-700/50" data-id="element-168">
        <div className="max-w-4xl mx-auto" data-id="element-169">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="bg-stone-950 rounded-3xl p-8 md:p-12 shadow-xl border border-stone-700 text-center" data-id="element-170">
            <motion.div variants={fadeInUp} className="w-16 h-16 bg-terracotta/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-171">
              <Shield className="w-8 h-8 text-terracotta" data-id="element-172" />
            </motion.div>

            <motion.h2 variants={fadeInUp} className="text-3xl font-serif text-stone-100 mb-6" data-id="element-173">
              Mi promesa personal: El riesgo es completamente mío
            </motion.h2>

            <motion.div variants={fadeInUp} className="space-y-4 text-stone-300 font-light text-lg max-w-2xl mx-auto mb-10" data-id="element-174">
              <p data-id="element-175">
                He pasado por el agotamiento que describes. Sé lo que cuesta
                confiar en algo nuevo cuando ya has fallado tantas veces.
              </p>
              <p data-id="element-176">Por eso, no quiero que el dinero sea tu preocupación.</p>
              <p className="font-medium text-terracotta text-xl py-2" data-id="element-177">
                Tienes 7 días completos para probar Desbloquea tu Energía
                Femenina.
              </p>
              <p data-id="element-178">
                Aplica los primeros microhábitos. Usa el Kit SOS un día de
                estrés. Haz el test del Mapa de Energía.
              </p>
              <p data-id="element-179">
                Si al séptimo día no sientes que algo está cambiando — aunque
                sea sutilmente — escríbeme. Te devuelvo el 100% de tu dinero en
                menos de 24 horas. Sin formularios, sin justificaciones, sin
                preguntas incómodas.
              </p>
              <p className="font-medium text-stone-100" data-id="element-180">
                Solo un mensaje. Y listo.
              </p>
              <p data-id="element-181">
                Porque estoy tan segura de que esto va a funcionar para ti, que
                prefiero asumir yo el riesgo.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="pt-8 border-t border-stone-700" data-id="element-182">
              <p className="text-stone-200 font-medium mb-6" data-id="element-183">
                ¿Es seguro comprar? Procesamos todos los pagos a través de
                Hotmart, la plataforma de productos digitales más segura del
                mundo.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left" data-id="element-184">
                <div className="flex items-start gap-3" data-id="element-185">
                  <Lock className="w-5 h-5 text-terracotta shrink-0 mt-0.5" data-id="element-186" />
                  <div data-id="element-187">
                    <p className="font-medium text-stone-100" data-id="element-188">
                      Encriptación bancaria
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-189">
                      Tus datos financieros están protegidos.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-190">
                  <Zap className="w-5 h-5 text-terracotta shrink-0 mt-0.5" data-id="element-191" />
                  <div data-id="element-192">
                    <p className="font-medium text-stone-100" data-id="element-193">
                      Entrega al instante
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-194">
                      Recibes el acceso en tu correo al segundo.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-195">
                  <CreditCard className="w-5 h-5 text-terracotta shrink-0 mt-0.5" data-id="element-196" />
                  <div data-id="element-197">
                    <p className="font-medium text-stone-100" data-id="element-198">Comodidad</p>
                    <p className="text-sm text-stone-400" data-id="element-199">
                      Paga con tu moneda local, tarjeta o PayPal.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 🔴 SECCIÓN 7: EL VALUE STACK */}
      <section id="pricing" className="py-32 px-6 bg-stone-950 scroll-mt-10" data-id="element-200">
        <div className="max-w-5xl mx-auto" data-id="element-201">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-202">
            <motion.div variants={fadeInUp} className="text-center mb-16" data-id="element-203">
              <h2 className="text-3xl md:text-5xl font-serif text-stone-100 mb-4" data-id="element-204">
                Lo que recibes al unirte hoy:
              </h2>
            </motion.div>

            <div className="bg-stone-900 rounded-3xl p-6 md:p-12 shadow-xl border border-stone-700 overflow-hidden" data-id="element-205">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-206">
                {/* Product Image */}
                <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" data-id="element-207">
                  <img src="https://cdn.magicpatterns.com/uploads/vSV8MoumX8Rj29K9H9LbJo/Imagen_bonos_energia_femenina_1.png" alt="Paquete completo Desbloquea tu Energía Femenina" className="w-full h-auto object-contain drop-shadow-2xl" loading="lazy" data-id="element-208" />
                </motion.div>

                {/* Product Details */}
                <div className="space-y-8" data-id="element-209">
                  <motion.div variants={fadeInUp} className="space-y-6" data-id="element-210">
                    <ValueItem title="GUÍA MAESTRA: Desbloquea tu Energía Femenina" value="$19.99" description="El protocolo de 21 días paso a paso para resetear tu biología y mente." isMain data-id="element-211" />
                    <ValueItem title="BONO #1: KIT SOS EN 5 MINUTOS" value="$17.00" description="Protocolos de emergencia para eliminar la niebla mental en medio de tu jornada laboral." data-id="element-212" />
                    <ValueItem title="BONO #2: MAPA DE ENERGÍA PERSONAL" value="$17.00" description="El test definitivo para encontrar tu 'fuga' de energía número uno y cerrarla en 7 días." data-id="element-213" />
                    <ValueItem title="BONO #3: GUIONES 'NO ME DA LA VIDA'" value="$17.00" description="30 frases para poner límites sin culpa y dejar de regalar tu energía a otros." data-id="element-214" />
                  </motion.div>

                  <div className="bg-stone-800 rounded-2xl border border-terracotta/20 relative overflow-hidden shadow-lg shadow-terracotta/5" data-id="element-215">
                    {/* Premium ribbon */}
                    <div className="bg-terracotta text-white p-5 text-center" data-id="element-216">
                      <div className="flex items-center justify-center gap-2 mb-2" data-id="element-217">
                        <span className="text-xl" data-id="element-218">⚠️</span>
                        <span className="font-bold uppercase tracking-widest text-sm" data-id="element-219">
                          Nota sobre el precio de $6.39
                        </span>
                      </div>
                      <p className="text-xs text-white/90 leading-relaxed max-w-sm mx-auto" data-id="element-220">
                        Este es el precio de lanzamiento que mantuve para las
                        primeras 200 mujeres que confíen en este programa.
                        Quería que el dinero no fuera una barrera para ninguna
                        mujer que lo necesita.
                      </p>
                      <p className="text-xs text-white/90 leading-relaxed mt-2 max-w-sm mx-auto" data-id="element-221">
                        Cuando completemos esta fase inicial, el precio sube a
                        $19.99 y los 3 bonos se venden por separado. No sé
                        cuántos accesos quedan disponibles a este precio. Si
                        estás leyendo esto, todavía puedes acceder.
                      </p>
                    </div>

                    <motion.div variants={fadeInUp} className="p-6 md:p-8" data-id="element-222">
                      {/* Itemized value list */}
                      <p className="text-stone-400 text-xs font-semibold uppercase tracking-widest mb-5" data-id="element-223">
                        Tu paquete de vitalidad incluye
                      </p>

                      <div className="space-y-3 mb-6" data-id="element-224">
                        <div className="flex justify-between items-center" data-id="element-225">
                          <span className="text-stone-200 text-sm" data-id="element-226">
                            Guía Maestra: Energía Femenina (21 días)
                          </span>
                          <span className="text-stone-400 line-through text-sm" data-id="element-227">
                            $19.99
                          </span>
                        </div>
                        <div className="flex justify-between items-center" data-id="element-228">
                          <span className="text-stone-200 text-sm" data-id="element-229">
                            Bono #1: Kit SOS en 5 Minutos
                          </span>
                          <span className="text-stone-400 line-through text-sm" data-id="element-230">
                            $17.00
                          </span>
                        </div>
                        <div className="flex justify-between items-center" data-id="element-231">
                          <span className="text-stone-200 text-sm" data-id="element-232">
                            Bono #2: Mapa de Energía Personal
                          </span>
                          <span className="text-stone-400 line-through text-sm" data-id="element-233">
                            $17.00
                          </span>
                        </div>
                        <div className="flex justify-between items-center" data-id="element-234">
                          <span className="text-stone-200 text-sm" data-id="element-235">
                            Bono #3: Guiones "No Me Da la Vida"
                          </span>
                          <span className="text-stone-400 line-through text-sm" data-id="element-236">
                            $17.00
                          </span>
                        </div>
                      </div>

                      {/* Total vs price */}
                      <div className="border-t border-stone-700 pt-4 mb-2" data-id="element-237">
                        <div className="flex justify-between items-center mb-1" data-id="element-238">
                          <span className="text-stone-400 text-sm" data-id="element-239">
                            Valor real del paquete:
                          </span>
                          <span className="text-stone-400 line-through text-base font-medium" data-id="element-240">
                            $70.99
                          </span>
                        </div>
                      </div>

                      <div className="bg-terracotta/5 border border-terracotta/15 rounded-xl p-5 mb-6 text-center" data-id="element-241">
                        <p className="text-stone-400 text-xs font-semibold uppercase tracking-widest mb-1" data-id="element-242">
                          Tú pagas hoy solamente
                        </p>
                        <div className="flex items-baseline justify-center gap-2" data-id="element-243">
                          <span className="text-5xl md:text-6xl font-serif text-terracotta font-bold leading-none" data-id="element-244">
                            $6.39
                          </span>
                          <span className="text-stone-400 text-sm" data-id="element-245">USD</span>
                        </div>
                        <p className="text-terracotta/80 text-xs mt-2 font-medium" data-id="element-246">
                          Ahorras $64.60 — 91% de descuento
                        </p>
                      </div>

                      <button onClick={handlePurchaseClick} className="w-full bg-terracotta text-white px-6 py-5 rounded-xl text-lg font-semibold hover:bg-terracotta-hover transition-all duration-300 shadow-lg shadow-terracotta/25 hover:shadow-terracotta/35 hover:scale-[1.02] mb-4 flex justify-center items-center gap-2" data-id="element-247">
                        <Lock className="w-5 h-5" data-id="element-248" />
                        COMPRAR AHORA CON ACCESO INMEDIATO
                      </button>

                      <div className="flex items-center justify-center gap-4 text-xs text-stone-500" data-id="element-249">
                        <span className="flex items-center gap-1" data-id="element-250">
                          <Lock className="w-3 h-3" data-id="element-251" /> Pago seguro
                        </span>
                        <span className="flex items-center gap-1" data-id="element-252">
                          <Zap className="w-3 h-3" data-id="element-253" /> Acceso inmediato
                        </span>
                        <span className="flex items-center gap-1" data-id="element-254">
                          <Shield className="w-3 h-3" data-id="element-255" /> Garantía 7 días
                        </span>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 🔴 NUEVA SECCIÓN: PREGUNTAS FRECUENTES (FAQ) */}
      <section className="py-32 px-6 bg-stone-900 border-t border-stone-700/50" data-id="element-256">
        <div className="max-w-3xl mx-auto" data-id="element-257">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-258">
            <motion.div variants={fadeInUp} className="text-center mb-16" data-id="element-259">
              <h2 className="text-3xl md:text-4xl font-serif text-stone-100 mb-4" data-id="element-260">
                Antes de comprar, respondo tus dudas más honestas
              </h2>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-stone-950 rounded-3xl p-6 md:p-10 border border-stone-700/50 shadow-xl" data-id="element-261">
              <AccordionItem question="¿Y si soy muy desorganizada y sé que lo voy a abandonar?" answer="Esto está diseñado exactamente para ti. Los microhábitos toman 2 minutos. Si pierdes un día, el día 5 empiezas donde dejaste. No hay 'empezar de cero'. No hay fracaso posible si lo intentas." data-id="element-262" />
              <AccordionItem question="¿Funciona si tengo problemas hormonales o tiroides?" answer="Este programa trabaja el sistema nervioso y los ritmos biológicos, no reemplaza un tratamiento médico. Pero muchas mujeres con condiciones hormonales reportan mejoras notables porque el estrés crónico agrava cualquier desequilibrio hormonal. Consulta con tu médico y usa esto como complemento." data-id="element-263" />
              <AccordionItem question="¿Necesito comprar suplementos o equipo especial?" answer="No. Absolutamente nada. Usas tu respiración, la luz natural, tu cuerpo y tu mente. Sin gastos adicionales." data-id="element-264" />
              <AccordionItem question="¿Es solo para mujeres jóvenes?" answer="No. Tenemos mujeres de 25 a 58 años aplicando este sistema con resultados. El sistema nervioso funciona igual a cualquier edad — solo necesita señales diferentes según el momento de vida." data-id="element-265" />
              <AccordionItem question="¿Cuándo recibo el material?" answer="Inmediatamente después de confirmar el pago. Recibirás un correo con el enlace de descarga en menos de 60 segundos. Si no lo ves, revisa tu carpeta de spam." data-id="element-266" />
              <AccordionItem question="¿Y si tengo dudas después de comprar?" answer="Puedes escribirme directamente. Hay un humano real detrás de este programa que quiere que funcione para ti." data-id="element-267" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 🔴 SECCIÓN 8: ÚLTIMA OPORTUNIDAD (CTA FINAL) */}
      <section id="cta-final" className="py-32 px-6 bg-stone-800 text-center relative overflow-hidden" data-id="element-268">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-stone-900 to-stone-800 opacity-50" data-id="element-269"></div>

        <div className="max-w-3xl mx-auto relative z-10" data-id="element-270">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-271">
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif text-stone-100 mb-8 leading-tight" data-id="element-272">
              La verdadera productividad no es hacer más cosas... es ser una
              mujer que se siente bien en su propia piel.
            </motion.h2>

            <motion.div variants={fadeInUp} className="space-y-6 text-lg text-stone-300 font-light mb-12" data-id="element-273">
              <p data-id="element-274">
                Imagina despertarte mañana sintiendo ligereza. Imagina llegar a
                las 6 de la tarde con ganas de un paseo, de una cena tranquila o
                de jugar con tus hijos, en lugar de colapsar en el sillón.
              </p>
              <p data-id="element-275">
                Esa vitalidad no se ha ido de tu cuerpo; está bloqueada. Y hoy
                tienes la llave para liberarla por el precio de un par de cafés.
              </p>
              <p className="font-medium text-stone-100 text-xl" data-id="element-276">
                No dejes que otro día de agotamiento pase de largo. El momento
                de recuperarte es hoy.
              </p>
            </motion.div>

            <motion.button variants={fadeInUp} onClick={handlePurchaseClick} className="w-full sm:w-auto bg-terracotta text-white px-10 py-5 rounded-full text-lg font-medium hover:bg-terracotta-hover transition-all duration-300 shadow-xl shadow-terracotta/30 hover:shadow-terracotta/40 hover:-translate-y-1 inline-flex items-center justify-center gap-2" data-id="element-277">
              🔥 SÍ, QUIERO COMPRAR "DESBLOQUEA TU ENERGÍA FEMENINA" POR $6.39
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 px-6 text-center bg-stone-950 border-t border-stone-700" data-id="element-278">
        <div className="max-w-4xl mx-auto" data-id="element-279">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-4" data-id="element-280">
            <button onClick={() => setModalContent('privacy')} className="text-stone-500 hover:text-terracotta transition-colors duration-300 text-sm font-medium" data-id="element-281">
              Política de Privacidad
            </button>
            <span className="hidden md:block text-stone-600" data-id="element-282">•</span>
            <button onClick={() => setModalContent('terms')} className="text-stone-500 hover:text-terracotta transition-colors duration-300 text-sm font-medium" data-id="element-283">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-stone-500 text-sm" data-id="element-284">
            &copy; {new Date().getFullYear()} Vitalidad. Hecho con intención.
          </p>
        </div>
      </footer>

      {/* MODAL */}
      <AnimatePresence data-id="element-285">
        {modalContent && <Modal onClose={() => setModalContent(null)} data-id="element-286">
            {modalContent === 'privacy' ? <PrivacyContent data-id="element-287" /> : <TermsContent data-id="element-288" />}
          </Modal>}
      </AnimatePresence>
    </div>;
}
// --- HELPER COMPONENTS ---
function EmpathyItem({
  title,
  description
}: {
  title: string;
  description: string;
}) {
  return <div className="flex items-start gap-4 p-5 rounded-2xl bg-stone-950/50 border border-stone-700/50 hover:border-terracotta/30 transition-colors duration-300" data-id="element-289">
      <div className="mt-1 bg-terracotta/10 p-1.5 rounded-full shrink-0" data-id="element-290">
        <CheckCircle2 className="w-5 h-5 text-terracotta" data-id="element-291" />
      </div>
      <div data-id="element-292">
        <h4 className="text-lg font-serif text-stone-100 mb-2" data-id="element-293">{title}</h4>
        <p className="text-stone-300 font-light leading-relaxed" data-id="element-294">
          {description}
        </p>
      </div>
    </div>;
}
function MechanismCard({
  icon,
  title,
  description,
  delay
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay,
    duration: 0.6
  }} className="bg-stone-950 p-6 md:p-8 rounded-2xl shadow-md shadow-stone-400/8 border border-stone-700 hover:border-terracotta/20 transition-all duration-300 flex flex-col items-center text-center" data-id="element-295">
      <div className="w-14 h-14 rounded-full bg-terracotta/10 border border-terracotta/20 flex items-center justify-center text-terracotta mb-6" data-id="element-296">
        {icon}
      </div>
      <h3 className="text-2xl font-serif mb-4 text-stone-100" data-id="element-297">{title}</h3>
      <p className="text-stone-300 leading-relaxed font-light" data-id="element-298">{description}</p>
    </motion.div>;
}
function TestimonialCard({
  headline,
  paragraphs,
  name,
  detail,
  tag,
  delay
}: {
  headline: string;
  paragraphs: string[];
  name: string;
  detail: string;
  tag: string;
  delay: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay,
    duration: 0.6
  }} className="bg-stone-950 p-6 md:p-8 rounded-2xl border border-terracotta/15 flex flex-col justify-between gap-6 shadow-sm" data-id="element-299">
      <div data-id="element-300">
        <div className="flex gap-1 mb-4" data-id="element-301">
          {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 text-terracotta fill-terracotta" data-id="element-302" />)}
        </div>
        <h4 className="text-lg font-serif text-stone-100 mb-4 font-medium leading-snug" data-id="element-303">
          "{headline}"
        </h4>
        <div className="space-y-3" data-id="element-304">
          {paragraphs.map((p, idx) => <p key={idx} className="text-stone-300 font-light leading-relaxed text-sm md:text-base" data-id="element-305">
              {p}
            </p>)}
        </div>
      </div>
      <div className="pt-5 border-t border-stone-700/50 mt-2" data-id="element-306">
        <div className="flex items-center gap-3 mb-3" data-id="element-307">
          <div className="w-10 h-10 rounded-full bg-terracotta/15 flex items-center justify-center text-terracotta font-serif text-lg shrink-0" data-id="element-308">
            {name.charAt(0)}
          </div>
          <div data-id="element-309">
            <p className="text-stone-200 font-medium text-sm" data-id="element-310">{name}</p>
            <p className="text-stone-500 text-xs" data-id="element-311">{detail}</p>
          </div>
        </div>
        <div className="inline-block bg-stone-900 text-stone-400 text-xs px-2.5 py-1 rounded-md border border-stone-800" data-id="element-312">
          {tag}
        </div>
      </div>
    </motion.div>;
}
function ValueItem({
  title,
  value,
  description,
  isMain = false
}: {
  title: string;
  value: string;
  description: string;
  isMain?: boolean;
}) {
  return <div className={`flex items-start gap-4 p-4 rounded-xl ${isMain ? 'bg-terracotta/5 border border-terracotta/20' : 'bg-stone-950/50 border border-stone-700/50'}`} data-id="element-313">
      <div className={`mt-1 p-2 rounded-lg shrink-0 ${isMain ? 'bg-terracotta text-white' : 'bg-stone-800 text-terracotta'}`} data-id="element-314">
        {isMain ? <BookOpen className="w-5 h-5" data-id="element-315" /> : <Gift className="w-5 h-5" data-id="element-316" />}
      </div>
      <div data-id="element-317">
        <div className="flex flex-wrap items-baseline gap-2 mb-1" data-id="element-318">
          <h4 className={`font-serif text-lg ${isMain ? 'text-terracotta font-bold' : 'text-stone-100'}`} data-id="element-319">
            {title}
          </h4>
          <span className="text-sm font-medium text-stone-400 bg-stone-800 px-2 py-0.5 rounded" data-id="element-320">
            (Valor {value})
          </span>
        </div>
        <p className="text-stone-300 font-light text-sm leading-relaxed" data-id="element-321">
          {description}
        </p>
      </div>
    </div>;
}
function Modal({
  children,
  onClose
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-50/40 backdrop-blur-sm" onClick={onClose} data-id="element-322">
      <motion.div initial={{
      scale: 0.95,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} exit={{
      scale: 0.95,
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="bg-stone-950 rounded-3xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden border border-stone-700" onClick={e => e.stopPropagation()} data-id="element-323">
        <div className="sticky top-0 bg-stone-950 border-b border-stone-700 px-6 py-4 flex justify-between items-center z-10" data-id="element-324">
          <h2 className="text-2xl font-serif text-stone-100" data-id="element-325">
            {children && (children as any).type === PrivacyContent ? 'Política de Privacidad' : 'Términos y Condiciones'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-stone-800 rounded-full transition-colors duration-300" data-id="element-326">
            <X className="w-5 h-5 text-stone-400" data-id="element-327" />
          </button>
        </div>
        <div className="px-6 py-8 overflow-y-auto max-h-[calc(80vh-80px)] text-stone-300" data-id="element-328">
          {children}
        </div>
      </motion.div>
    </motion.div>;
}
// Keep content components exactly as they were
function PrivacyContent() {
  return <div className="prose prose-stone max-w-none" data-id="element-329">
      <p className="text-stone-400 mb-6" data-id="element-330">
        Última actualización: {new Date().toLocaleDateString('es-ES')}
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-331">
        1. Información que Recopilamos
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-332">
        Al adquirir "Desbloquea tu Energía Femenina", recopilamos únicamente la
        información necesaria para procesar tu compra y entregarte el producto
        digital: nombre, correo electrónico e información de pago (procesada de
        forma segura por nuestro proveedor de pagos).
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-333">
        2. Uso de la Información
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-334">Utilizamos tu información para:</p>
      <ul className="list-disc pl-6 text-stone-300 mb-6 space-y-2" data-id="element-335">
        <li data-id="element-336">Procesar y entregar tu compra del ebook</li>
        <li data-id="element-337">Enviarte el acceso inmediato al contenido digital</li>
        <li data-id="element-338">Proporcionar soporte relacionado con tu compra</li>
        <li data-id="element-339">
          Enviarte actualizaciones importantes sobre el producto (opcional)
        </li>
      </ul>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-340">
        3. Protección de Datos
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-341">
        Implementamos medidas de seguridad para proteger tu información
        personal. No compartimos, vendemos ni alquilamos tus datos a terceros.
        Tu información de pago es procesada de forma segura por proveedores
        certificados y nunca almacenamos datos de tarjetas de crédito.
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-342">
        4. Tus Derechos
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-343">
        Tienes derecho a acceder, corregir o eliminar tu información personal en
        cualquier momento. Para ejercer estos derechos, contáctanos y
        responderemos en un plazo de 48 horas.
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-344">5. Cookies</h3>
      <p className="text-stone-300 mb-6" data-id="element-345">
        Utilizamos cookies esenciales para el funcionamiento del sitio y para
        mejorar tu experiencia de compra. No utilizamos cookies de seguimiento
        publicitario.
      </p>
    </div>;
}
function TermsContent() {
  return <div className="prose prose-stone max-w-none" data-id="element-346">
      <p className="text-stone-400 mb-6" data-id="element-347">
        Última actualización: {new Date().toLocaleDateString('es-ES')}
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-348">
        1. Aceptación de Términos
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-349">
        Al adquirir "Desbloquea tu Energía Femenina", aceptas estos términos y
        condiciones. Este ebook es un producto digital diseñado para uso
        personal y no puede ser redistribuido, revendido o compartido.
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-350">
        2. Licencia de Uso
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-351">
        Se te otorga una licencia personal, no transferible y no exclusiva para
        usar el ebook y sus materiales complementarios (checklists, guías,
        tracker). No puedes:
      </p>
      <ul className="list-disc pl-6 text-stone-300 mb-6 space-y-2" data-id="element-352">
        <li data-id="element-353">Compartir, copiar o distribuir el contenido</li>
        <li data-id="element-354">Modificar o crear trabajos derivados</li>
        <li data-id="element-355">Usar el contenido con fines comerciales</li>
        <li data-id="element-356">Revender o transferir tu acceso a terceros</li>
      </ul>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-357">
        3. Entrega del Producto
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-358">
        Recibirás acceso inmediato al ebook y todos los bonuses digitales tras
        completar tu compra. El acceso se enviará al correo electrónico
        proporcionado. Si no recibes el acceso en 10 minutos, verifica tu
        carpeta de spam o contáctanos.
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-359">
        4. Garantía de Satisfacción
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-360">
        Ofrecemos una garantía de satisfacción de 14 días. Si el contenido no
        cumple con tus expectativas, contáctanos para solicitar un reembolso
        completo. Solo pedimos que nos compartas brevemente tu experiencia para
        mejorar el producto.
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-361">
        5. Descargo de Responsabilidad
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-362">
        Este ebook proporciona información educativa sobre bienestar y
        microhábitos. No sustituye el consejo médico profesional. Consulta con
        un profesional de la salud antes de realizar cambios significativos en
        tu estilo de vida, especialmente si tienes condiciones médicas
        preexistentes.
      </p>

      <h3 className="text-xl font-serif text-stone-100 mb-4" data-id="element-363">
        6. Propiedad Intelectual
      </h3>
      <p className="text-stone-300 mb-6" data-id="element-364">
        Todo el contenido del ebook, incluyendo texto, imágenes, diseño y
        materiales complementarios, está protegido por derechos de autor.
        Cualquier uso no autorizado constituye una violación de estos derechos.
      </p>
    </div>;
}
function AccordionItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-stone-700/50 last:border-0" data-id="element-365">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left gap-4 group" data-id="element-366">
        <h4 className="text-lg font-serif text-stone-100 group-hover:text-terracotta transition-colors" data-id="element-367">
          {question}
        </h4>
        <ChevronDown className={`w-5 h-5 text-stone-400 transition-transform duration-300 shrink-0 ${isOpen ? 'rotate-180' : ''}`} data-id="element-368" />
      </button>
      <AnimatePresence data-id="element-369">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-370">
            <p className="pb-6 text-stone-300 font-light leading-relaxed" data-id="element-371">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}